<template>
  <div>
    <b-row>
      <b-col cols="12" lg="3" class="mb-2">
        <b-button variant="outline-primary" class="mb-2" @click="$router.push('/crm/lead')">
          <feather-icon icon="ArrowLeftIcon" class="mr-50" />
          Back to Leads
        </b-button>

        <b-card>
          <div class="text-center">
            <b-avatar size="70px" variant="light-primary" class="mb-1">
              {{ getInitials(lead.name) }}
            </b-avatar>
            <h5 class="mb-50">{{ lead.name || "NA" }}</h5>
            <p class="text-muted mb-0">{{ lead.email || "NA" }}</p>
          </div>
        </b-card>

        <b-card title="Quick Actions">
          <div class="d-flex flex-wrap">
            <b-button size="sm" variant="primary" class="mr-50 mb-50" disabled>Note</b-button>
            <b-button size="sm" variant="info" class="mr-50 mb-50" disabled>Email</b-button>
            <b-button size="sm" variant="success" class="mr-50 mb-50" disabled>Call</b-button>
            <b-button size="sm" variant="warning" class="mr-50 mb-50" disabled>Follow Up</b-button>
            <b-button size="sm" variant="dark" class="mb-50" disabled>Meetings</b-button>
          </div>
        </b-card>

        <b-card no-body>
          <b-card-header class="d-flex justify-content-between align-items-center">
            <span>About this Contact</span>
            <b-button variant="flat-secondary" size="sm" @click="aboutOpen = !aboutOpen">
              <feather-icon :icon="aboutOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'" />
            </b-button>
          </b-card-header>
          <b-collapse :visible="aboutOpen">
            <b-card-body class="small">
              <p class="mb-50"><strong>Email:</strong> {{ lead.email || "NA" }}</p>
              <p class="mb-50"><strong>Owner:</strong> {{ getAssignedToName }}</p>
              <p class="mb-50"><strong>Status:</strong> {{ getLeadStatus }}</p>
              <p class="mb-50"><strong>Stage:</strong> {{ getLeadStatus }}</p>
              <p class="mb-50"><strong>Category:</strong> {{ getLeadCategory }}</p>
              <p class="mb-50"><strong>Source:</strong> {{ getLeadSource }}</p>
              <p class="mb-0"><strong>Product:</strong> {{ lead.product_category || "NA" }}</p>
            </b-card-body>
          </b-collapse>
        </b-card>
      </b-col>

      <b-col cols="12" lg="6" class="mb-2">
        <b-card>
          <b-form-input
            v-model="searchText"
            placeholder="Search inside profile (UI placeholder)"
            class="mb-2"
          />

          <b-tabs content-class="pt-2">
            <b-tab title="Overview" active>
              <lead-profile-overview :selected-id="leadId" />
            </b-tab>
            <b-tab title="Notes">
              <b-alert show variant="light">Notes API will be wired in Phase 2.</b-alert>
            </b-tab>
            <b-tab title="Emails">
              <b-alert show variant="light">Email tab will be wired in a later phase.</b-alert>
            </b-tab>
            <b-tab title="Follow up">
              <lead-profile-followup :followups="followups" :users="users" />
            </b-tab>
            <b-tab title="Meetings">
              <b-alert show variant="light">Meetings API will be wired in Phase 3.</b-alert>
            </b-tab>
            <b-tab title="AI Suggestion">
              <b-alert show variant="light">AI suggestion endpoint is optional and not enabled yet.</b-alert>
            </b-tab>
          </b-tabs>
        </b-card>
      </b-col>

      <b-col cols="12" lg="3">
        <b-card title="Company Details">
          <p class="mb-50"><strong>Company:</strong> {{ getCompanyName }}</p>
          <p class="mb-50"><strong>Address:</strong> {{ lead.address || "NA" }}</p>
          <p class="mb-50"><strong>City:</strong> {{ lead.city || "NA" }}</p>
          <p class="mb-0"><strong>State:</strong> {{ lead.state || "NA" }}</p>
        </b-card>

        <b-card title="Quotations">
          <b-alert show variant="light" class="mb-0">
            Quotations list will be added in Phase 3.
          </b-alert>
        </b-card>

        <b-card title="Attachments">
          <b-alert show variant="light" class="mb-0">
            Attachments will be added in a later phase.
          </b-alert>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import axios from "@/components/axios";
import {
  BRow,
  BCol,
  BCard,
  BCardBody,
  BCardHeader,
  BButton,
  BCollapse,
  BTabs,
  BTab,
  BAlert,
  BFormInput,
  BAvatar,
} from "bootstrap-vue";
import LeadProfileOverview from "./LeadProfileOverview.vue";
import LeadProfileFollowup from "./LeadProfileFollowup.vue";

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BCardBody,
    BCardHeader,
    BButton,
    BCollapse,
    BTabs,
    BTab,
    BAlert,
    BFormInput,
    BAvatar,
    LeadProfileOverview,
    LeadProfileFollowup,
  },
  data() {
    return {
      accessToken: localStorage.getItem("accessToken"),
      baseApi: process.env.VUE_APP_APIENDPOINT,
      lead: {},
      users: [],
      leadStages: [],
      aboutOpen: true,
      searchText: "",
    };
  },
  computed: {
    leadId() {
      return this.$route.params.id;
    },
    followups() {
      const items = this.lead.followUpData || [];
      return [...items].sort((a, b) => new Date(b.datetime) - new Date(a.datetime));
    },
    getLeadSource() {
      if (Array.isArray(this.lead.leadsource) && this.lead.leadsource.length > 0) {
        return this.lead.leadsource[0].name || "NA";
      }
      if (this.lead.leadsource && this.lead.leadsource.name) {
        return this.lead.leadsource.name;
      }
      return "NA";
    },
    getLeadCategory() {
      if (Array.isArray(this.lead.leadcategory) && this.lead.leadcategory.length > 0) {
        return this.lead.leadcategory[0].name || "NA";
      }
      if (this.lead.leadcategory && this.lead.leadcategory.name) {
        return this.lead.leadcategory.name;
      }
      return "NA";
    },
    getLeadStatus() {
      if (this.lead.status && typeof this.lead.status === "object" && this.lead.status.name) {
        return this.lead.status.name;
      }
      const statusId = parseInt(this.lead.status, 10);
      const status = this.leadStages.find((item) => item.id === statusId);
      return status ? status.name : this.lead.status || "NA";
    },
    getAssignedToName() {
      if (this.lead.assignedToUser && this.lead.assignedToUser.fullname) {
        return this.lead.assignedToUser.fullname;
      }
      if (Array.isArray(this.lead.assignto) && this.lead.assignto.length > 0) {
        return this.lead.assignto[0].fullname || this.lead.assignto[0].name || "NA";
      }
      return "NA";
    },
    getCompanyName() {
      return this.lead.lead_companyname || this.lead.previous_company || "NA";
    },
  },
  watch: {
    "$route.params.id": {
      immediate: true,
      handler() {
        this.loadLead();
      },
    },
  },
  mounted() {
    this.users = this.$store.state.app.all_users || [];
    this.getLeadStages();
  },
  methods: {
    async loadLead() {
      if (!this.leadId) return;
      await axios({
        method: "GET",
        url: `${this.baseApi}/getleadById/${this.leadId}`,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.accessToken}`,
        },
      })
        .then((response) => {
          this.lead = Array.isArray(response.data.data) ? response.data.data[0] : response.data.data || {};
        })
        .catch((error) => {
          console.log(error, "error");
        });
    },
    async getLeadStages() {
      await axios({
        method: "GET",
        url: `${this.baseApi}/leadstage`,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.accessToken}`,
        },
      })
        .then((response) => {
          this.leadStages = response.data.data.data || response.data.data || [];
        })
        .catch((error) => console.log(error, "error"));
    },
    getInitials(name) {
      if (!name) return "L";
      const parts = name.trim().split(" ").filter(Boolean);
      if (!parts.length) return "L";
      return parts.slice(0, 2).map((part) => part[0].toUpperCase()).join("");
    },
  },
};
</script>
